// How a dashboard change reads: rates move in whole percentages, counts in whole numbers.

const MINUS = "−";

// Text colours with at least 4.5:1 contrast on the change backgrounds and on white.
const GOOD = "#237804";
const BAD = "#cf1322";
const NEUTRAL = "#595959";

export const comparisonOptions = [
  { value: "lastYear", label: "Last year" },
  { value: "previous", label: "Previous period" },
];

// 365 days back is the same dates as last year, and year to date has no useful period before it.
export const lastYearOnlyRanges = ["365d", "YTD"];

const comparisonPhrases = {
  lastYear: "vs. same period last year",
  previous: "vs. previous period",
};

const formatValue = (value, isPercentage) =>
  isPercentage ? `${Math.round(value * 100)}%` : `${value}`;

/**
 * Rounds a change the way it is shown, or returns null when there is nothing
 * to compare with. A change that rounds to zero still shows, as "0".
 */
export const describeChange = ({ delta, isPercentage }) => {
  if (delta == null) return null;

  const amount = Math.round(isPercentage ? delta * 100 : delta) || 0;
  const direction = amount > 0 ? "up" : amount < 0 ? "down" : "none";
  const sign = amount > 0 ? "+" : amount < 0 ? MINUS : "";
  const unit = isPercentage ? "%" : "";

  return { amount, direction, text: `${sign}${Math.abs(amount)}${unit}` };
};

/**
 * What a screen reader announces instead of the arrow and number,
 * e.g. "Down 2% vs. same period last year (39% → 37%)".
 */
export const spokenChange = ({
  change,
  comparison,
  isPercentage,
  value,
  earlierValue,
}) => {
  const phrase = comparisonPhrases[comparison];
  const values =
    earlierValue == null
      ? ""
      : ` (${formatValue(earlierValue, isPercentage)} → ${formatValue(value, isPercentage)})`;

  if (change.direction === "none") return `No change ${phrase}${values}`;

  const size = Math.abs(change.amount);
  const unit = isPercentage ? "%" : "";
  const verb = change.direction === "up" ? "Up" : "Down";
  return `${verb} ${size}${unit} ${phrase}${values}`;
};

// A rise is good unless lower is better for the metric, as with declines.
const isImprovement = (direction, invertDelta) =>
  (direction === "up") !== Boolean(invertDelta);

export const changeColor = (direction, invertDelta) => {
  if (direction === "none") return NEUTRAL;
  return isImprovement(direction, invertDelta) ? GOOD : BAD;
};

export const changeBackground = (direction, invertDelta) => {
  if (direction === "none") return "#fafafa";
  return isImprovement(direction, invertDelta) ? "#f6ffed" : "#fff1f0";
};
